/** The `accountErrors` area: the refusals of accounts, operators, agent keys and connectors, contacts and invitations (src/server/account-*.ts, machine-keys.ts, connector-grants.ts, operators.ts, contacts.ts, work-item-invites.ts, the account, admin and join actions), as descriptors a surface formats
 *  (docs/I18N.md §6). The English is the refusal's `message`, unchanged. */
export const accountErrors = {
  /** Account deletion (src/domain/account-deletion.ts). {names}: group names, joined with ", ". */
  'accountErrors.deletion.ownFromAccountPage': 'Delete your own account from your account page.',
  'accountErrors.deletion.alreadyDeleted': 'This account is already deleted.',
  'accountErrors.deletion.lastOperator':
    'The last operator cannot be deleted. Make someone else an operator first.',
  'accountErrors.deletion.onlyOwnerSelf':
    'You are the only owner of {names}. Make another member an owner first.',
  'accountErrors.deletion.onlyOwnerOther':
    'This person is the only owner of {names}. Another owner is needed first.',
  'accountErrors.deletion.scrubbed': 'This account was scrubbed. It cannot be restored.',
  'accountErrors.deletion.notDeleted': 'This account is not deleted.',
  /** Account links (src/domain/account-invites.ts). {roles}: role keys, joined with ", ". */
  'accountErrors.label.required': 'A label is required.',
  'accountErrors.link.labelTooLong': 'Label: at most {max} characters.',
  'accountErrors.link.uses': 'Uses: a whole number from 1 to {max}.',
  'accountErrors.link.expiry': 'Expiry: a whole number of days from 1 to {max}.',
  'accountErrors.link.groupRole': 'Group role: one of {roles}.',
  /** An agent key's scope (validateMachineScope, src/domain/machine-scope.ts). */
  'accountErrors.keyScope.missing':
    'A key states what it may do and where; full authority is its own choice.',
  'accountErrors.keyScope.unreadable': 'Unreadable key scope.',
  'accountErrors.keyScope.access': 'A key may see, see and change, or see, change and share.',
  'accountErrors.keyScope.rootIds': 'The items a key reaches are named by their ids.',
  'accountErrors.keyScope.noRoots': 'Pick at least one item, or none to reach everything you can.',
  'accountErrors.keyScope.tooManyRoots':
    'A key reaches at most {max} items and what is below them.',
  'accountErrors.keyScope.where':
    "A key states where it acts: 'everything', or some groups and your own items.",
  'accountErrors.keyScope.own': 'Whether a key reaches your own items is yes or no.',
  'accountErrors.keyScope.groupIds': 'The groups a key reaches are named by their ids.',
  'accountErrors.keyScope.noGroups': 'Pick at least one group, or your own items.',
  'accountErrors.keyScope.tooManyGroups': 'A key reaches at most {max} groups.',
  /** A name that resolves to nothing the actor may reach. A hidden one answers the same (no oracle). */
  'accountErrors.noSuch.item': 'No such item.',
  'accountErrors.noSuch.group': 'No such group.',
  'accountErrors.noSuch.account': 'No such account.',
  'accountErrors.noSuch.app': 'No such app.',
  'accountErrors.noSuch.key': 'No such key.',
  'accountErrors.noSuch.link': 'No such link.',
  'accountErrors.noSuch.contact': 'No such contact.',
  'accountErrors.account.deleted': 'This account is deleted.',
  'accountErrors.deletion.selfOnly': 'Only the person can delete their own account.',
  'accountErrors.deletion.inPerson':
    'An operator deletes an account in person, never through an agent key.',
  /** The operator plane (src/server/operators.ts). */
  'accountErrors.operator.only': 'Operators only.',
  'accountErrors.operator.removeSelf':
    'You cannot remove yourself as an operator; ask another operator.',
  'accountErrors.operator.removeLast': 'The last operator cannot be removed.',
  /** One answer for every dead account link (no oracle). */
  'accountErrors.link.notValid': 'This link is not valid.',
  'accountErrors.link.seatOwnerOnly': 'Only an owner of the group can seat newcomers in it.',
  /** Item invitations (src/server/work-item-invites.ts). notValid is one answer for every dead token (no oracle). */
  'accountErrors.invite.notValid': 'This invite is no longer valid.',
  'accountErrors.invite.badEmail': 'That does not look like an email address.',
  'accountErrors.invite.addressBarred': 'That address cannot be invited.',
  'accountErrors.invite.badLifetime': 'Invalid invite lifetime.',
  'accountErrors.invite.rateLimited':
    'Too many invites sent recently. Please wait a while before inviting more people.',
  'accountErrors.invite.notFound': 'Invite not found.',
  'accountErrors.invite.revokeForbidden':
    'Only the inviter or an owner of the item can revoke an invite.',
  'accountErrors.invite.notPending': 'Invite is not pending.',
  'accountErrors.invite.personOnly': 'Only a person can claim an invite.',
  /** Agent keys (src/server/machine-keys.ts). */
  'accountErrors.key.mintPersonOnly': 'Only a person can mint an agent key.',
  'accountErrors.key.machineId':
    'Machine id: lowercase letters, digits and dashes, 2-32 characters.',
  'accountErrors.key.liveExists': 'A live key for "{machineId}" exists; revoke it first.',
  'accountErrors.key.revokePersonOnly': 'Only a person can revoke an agent key.',
  /** Connected apps (src/server/connector-grants.ts). */
  'accountErrors.app.allowPersonOnly': 'Only a person can allow an app.',
  'accountErrors.app.removePersonOnly': 'Only a person can remove an app.',
  /** Contacts (src/server/contacts.ts). noRequest and noSuch.contact answer hit and miss alike (docs/REACHABILITY.md). */
  'accountErrors.contact.personOnly': 'Only a person has contacts.',
  'accountErrors.contact.rateLimited':
    'Too many contact requests recently. Please wait a while before sending more.',
  'accountErrors.contact.identifierNeeded': 'A handle or an address is needed.',
  'accountErrors.contact.self': 'That is you.',
  'accountErrors.contact.noRequest': 'No request to accept.',
  /** The /account page (src/app/account/actions.ts, deletion-actions.ts). */
  'accountErrors.password.tooShort': 'A password needs at least {min} characters.',
  'accountErrors.password.alreadySet':
    'This account already has a password. Change it with your current one.',
  'accountErrors.address.personOnly': 'Only a person has an address.',
  'accountErrors.address.invalid': 'That is not an address.',
  'accountErrors.address.unchanged': 'That is already your address.',
  'accountErrors.address.alreadyVerified': 'This address is already verified.',
  'accountErrors.push.unconfigured': 'This instance cannot send notifications.',
  'accountErrors.push.badSubscription': 'That subscription is not usable.',
  'accountErrors.push.noDevice': 'No device was named.',
  'accountErrors.deletion.personOnly': 'Only a person has an account.',
  'accountErrors.deletion.addressMismatch': 'That is not the address of this account.',
  'accountErrors.link.joinPersonOnly': 'Only a person can join.',
  /** The machine-key request lane (src/lib/machine-request.ts). */
  'accountErrors.agent.unauthorized':
    'Not signed in. Send Authorization: Bearer <agent key>, or connect through OAuth (the 401 names where); a revoked key or a removed connection stops working at once.',
  'accountErrors.agent.bodyNotJson': 'The body is not JSON.',
  'accountErrors.agent.bodyTooLarge': 'The body is larger than 2 MiB.',
} as const;
